import type { FC } from 'react';
import { useCallback, useState, useEffect, useRef } from 'react';
import loadable from '@loadable/component';
import { useTracking } from 'react-tracking';
import { useToggleHotjarFeedback } from '@/hooks/useToggleHotjarFeedback';
const GuestRegistrationModal = loadable(
  () => import('@components/GuestRegistrationModal')
);

interface Props {
  /**
   * `pixelLimit` is the number of pixels a user can scroll down (`window.scrollY`) before the `GuestRegistrationModal` covers the entire screen and requires them to log in or register.
   *
   * This value defaults to `500`
   */
  pixelLimit?: number;
}

export const GuestRegistrationModalOnScroll: FC<Props> = ({
  pixelLimit = 500,
}) => {
  const trackLimitExceeded = useRef(false);
  const { trackEvent } = useTracking();
  const [showModal, setShowModal] = useState(false);
  const { showHotjarButton, hideHotjarButton } = useToggleHotjarFeedback();
  const handleScroll = useCallback(() => {
    if (window.scrollY > pixelLimit) {
      setShowModal(true);
      hideHotjarButton();
      /**
       * Trigger one event when user hits limit
       */
      if (trackLimitExceeded.current === false) {
        trackLimitExceeded.current = true;
        trackEvent({
          type: 'track',
          subject: 'GuestRegistrationModal',
          modifier: 'scrollLimit',
          action: 'exceeded',
        });
      }
    } else if (showModal) {
      setShowModal(false);
      showHotjarButton();
    }
  }, [showModal, pixelLimit, showHotjarButton, hideHotjarButton]);
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);
  if (showModal === false) return null;
  return (
    <GuestRegistrationModal
      onRegisterSuccess={() => {
        trackEvent({
          type: 'track',
          subject: 'guestRegistrationModal',
          modifier: 'registration',
          action: 'success',
        });
      }}
    />
  );
};
